/**
 * 완성된 시간표 엑셀을 DB에 넣기 전에 형식·충돌을 모아 사용자에게 보여 준다.
 * 한 선생님이 같은 시간에 두 학급에 있으면 unique_teacher_schedule 때문에 업로드가 실패한다.
 */

export const DAY_MARKERS = ["월", "화", "수", "목", "금", "토", "일"] as const;
export type DayMarker = (typeof DAY_MARKERS)[number];

export type ExcelImportIssueCode =
    | "UNRECOGNIZED_CELL"
    | "SHARED_LESSON_CONFIRMATION"
    | "MULTIPLE_SHEETS"
    | "NO_DAY_HEADER"
    | "NO_PERIOD_ROW"
    | "NO_CLASS_DATA"
    | "NO_LESSONS"
    | "TEACHER_DOUBLE_BOOKED"
    | "CLASSROOM_DOUBLE_BOOKED"
    | "SUBJECT_EQUALS_TEACHER"
    | "MISSING_SUBJECT"
    | "MISSING_TEACHER";

export type ExcelImportIssue = {
    code: ExcelImportIssueCode;
    message: string;
    hint: string;
    day?: string;
    period?: number;
    teacherName?: string;
    classroom?: string;
    subjectName?: string;
    excelRefs?: string[];
};

export type ParsedLesson = {
    row: number;
    col: number;
    classroom: string;
    day: string;
    period: number;
    subjectName: string;
    teacherName: string;
    parts: string[];
    raw: string;
    /** Explicitly selected external-instructor row; imported as a fixed lesson. */
    sharedFixed?: boolean;
};

export type TimetableImportOptions = { sharedTeacherRows?: number[] };
export type SharedTeacherRow = {
    row: number;
    label: string;
    classrooms: string[];
    slotCount: number;
    lessonCount: number;
    selected: boolean;
};

export type InspectTimetableResult = {
    issues: ExcelImportIssue[];
    lessons: ParsedLesson[];
    classroomNames: string[];
    classCol: number;
    daysRowIdx: number;
    periodsRowIdx: number;
    dataStartRow: number;
    format?: string;
    sheetName?: string;
    availableSheets?: string[];
    sharedTeacherRows?: SharedTeacherRow[];
};

export class ExcelImportValidationError extends Error {
    readonly issues: ExcelImportIssue[];

    constructor(issues: ExcelImportIssue[]) {
        const n = issues.length;
        super(
            n === 1
                ? "엑셀에서 1곳을 수정해야 업로드할 수 있습니다."
                : `엑셀에서 ${n}곳을 수정해야 업로드할 수 있습니다.`
        );
        this.name = "ExcelImportValidationError";
        this.issues = issues;
    }
}

export function isExcelImportValidationError(e: unknown): e is ExcelImportValidationError {
    return e instanceof ExcelImportValidationError;
}

export const EXCEL_IMPORT_ISSUE_GROUPS: {
    codes: ExcelImportIssueCode[];
    heading: string;
    description: string;
}[] = [
    {
        codes: ["SHARED_LESSON_CONFIRMATION"],
        heading: "여러 학급이 함께 적힌 수업",
        description: "외부강사 여러 명이 맡는 행이면 위에서 공동 수업으로 선택해 주세요. 실제 교사 한 명의 수업이면 엑셀의 학반을 확인해 주세요.",
    },
    {
        codes: ["TEACHER_DOUBLE_BOOKED"],
        heading: "같은 시간에 두 학급에 들어간 선생님",
        description: "한 선생님은 같은 요일·교시에 한 학급에만 들어갈 수 있습니다.",
    },
    {
        codes: ["CLASSROOM_DOUBLE_BOOKED"],
        heading: "한 학급의 같은 시간에 수업이 두 개",
        description: "한 학급의 같은 요일·교시에는 수업이 하나만 있어야 합니다.",
    },
    {
        codes: ["SUBJECT_EQUALS_TEACHER", "MISSING_SUBJECT", "MISSING_TEACHER"],
        heading: "칸 내용이 형식과 다름",
        description: "각 칸은 첫 줄에 과목, 둘째 줄에 교사명을 적어 주세요. 예: 영어 / 김정영",
    },
    {
        codes: ["NO_DAY_HEADER", "NO_PERIOD_ROW", "NO_CLASS_DATA", "NO_LESSONS", "UNRECOGNIZED_CELL", "MULTIPLE_SHEETS"],
        heading: "시간표 구조 확인",
        description: "요일·교시와 학급 또는 교사 이름이 표시된 시간표인지 확인해 주세요.",
    },
];

const DAY_FULL: Record<string, string> = {
    월: "월요일",
    화: "화요일",
    수: "수요일",
    목: "목요일",
    금: "금요일",
    토: "토요일",
    일: "일요일",
};

export function asString(v: unknown): string {
    if (v === null || v === undefined) return "";
    return String(v);
}

export function isBlank(s: string): boolean {
    const t = s.trim();
    return t === "" || t.toLowerCase() === "nan";
}

export function colToLetter(col: number): string {
    let n = col + 1;
    let s = "";
    while (n > 0) {
        const rem = (n - 1) % 26;
        s = String.fromCharCode(65 + rem) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

export function excelRef(row: number, col: number): string {
    return `${colToLetter(col)}${row + 1}`;
}

export function classLabel(name: string): string {
    const t = name.trim();
    if (!t) return name;
    return /반$/.test(t) ? t : `${t}반`;
}

export function formatDayPeriod(day: string, period: number): string {
    return `${DAY_FULL[day] ?? `${day}요일`} ${period}교시`;
}

/**
 * 엑셀 셀 한 칸을 (subjectName, teacherName) 으로 분리한다.
 * Django 와 동일한 휴리스틱:
 *  - `_x000D_` 등 carriage return 토큰 제거
 *  - 괄호를 공백으로 치환 ("국어(홍길동)" → "국어 홍길동")
 *  - 줄바꿈/2칸 이상 공백 우선 분리
 *  - 위로 분리 못하면 단일 공백으로 분리
 *  - 분리 결과의 첫 토큰이 과목, 마지막 토큰이 교사 (1개면 교사는 "미지정")
 */
export function parseCell(raw: string): { subjectName: string; teacherName: string; parts: string[] } {
    let v = raw.replace(/_x[0-9a-fA-F]{4}_/g, "");
    v = v.replace(/[()]/g, " ").trim();

    let parts = v.split(/[\n\r]+|\s{2,}/).map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 1) {
        parts = v.split(/\s+/).map((p) => p.trim()).filter(Boolean);
    }
    const subjectName = (parts[0] || "").trim();
    const teacherName = parts.length > 1 ? parts[parts.length - 1].trim() : "미지정";
    return { subjectName, teacherName, parts };
}

function isDayHeaderCell(raw: unknown): boolean {
    const t = asString(raw).trim();
    if (!t) return false;
    return DAY_MARKERS.some((d) => t === d || t.startsWith(`${d}요일`) || t.startsWith(`${d}요`));
}

function findDaysRowIdx(aoa: unknown[][]): number {
    for (let i = 0; i < Math.min(10, aoa.length); i++) {
        const row = aoa[i] || [];
        if (row.some((v) => isDayHeaderCell(v))) {
            return i;
        }
    }
    return -1;
}

function buildDayMap(dayRow: unknown[]): Map<number, string> {
    const dayMap = new Map<number, string>();
    let currentDay: string | null = null;
    for (let col = 0; col < dayRow.length; col++) {
        const v = asString(dayRow[col]).trim();
        if (v && v.toLowerCase() !== "nan") {
            if (isDayHeaderCell(v)) currentDay = v.trim()[0];
            else if (DAY_MARKERS.includes(v.trim()[0] as DayMarker)) currentDay = v.trim()[0];
        }
        if (currentDay) dayMap.set(col, currentDay);
    }
    return dayMap;
}

function buildPeriodMap(periodRow: unknown[]): Map<number, number> {
    const periodMap = new Map<number, number>();
    for (let col = 0; col < periodRow.length; col++) {
        const raw = asString(periodRow[col]);
        const num = parseInt(parseFloat(raw).toString(), 10);
        if (Number.isFinite(num) && num > 0) periodMap.set(col, num);
    }
    return periodMap;
}

function findClassCol(aoa: unknown[][], dataStartRow: number): number {
    const classroomCellRe = /^\d+[^\d]+\d+$/;
    const classroomCellOnlyDigits = /^\d+$/;
    for (let col = 0; col < 10; col++) {
        for (let row = dataStartRow; row < Math.min(dataStartRow + 5, aoa.length); row++) {
            const v = asString((aoa[row] || [])[col]).trim();
            if (classroomCellRe.test(v) || classroomCellOnlyDigits.test(v)) {
                return col;
            }
        }
    }
    return 0;
}

function slotLabel(entries: { classroom: string; subjectName: string }[]): string {
    return entries
        .map((e) => `${classLabel(e.classroom)}(${e.subjectName || "과목 없음"})`)
        .join(", ");
}

function daySortKey(day: string): number {
    const i = DAY_MARKERS.indexOf(day as DayMarker);
    return i === -1 ? 99 : i;
}

function sortIssues(issues: ExcelImportIssue[]): ExcelImportIssue[] {
    return [...issues].sort((a, b) => {
        const day = daySortKey(a.day ?? "") - daySortKey(b.day ?? "");
        if (day !== 0) return day;
        const period = (a.period ?? 0) - (b.period ?? 0);
        if (period !== 0) return period;
        return (a.teacherName ?? a.classroom ?? "").localeCompare(b.teacherName ?? b.classroom ?? "", "ko");
    });
}

export function inspectTimetableAoa(aoa: unknown[][]): InspectTimetableResult {
    const empty: InspectTimetableResult = {
        issues: [],
        lessons: [],
        classroomNames: [],
        classCol: 0,
        daysRowIdx: -1,
        periodsRowIdx: -1,
        dataStartRow: -1,
    };

    const daysRowIdx = findDaysRowIdx(aoa);
    if (daysRowIdx === -1) {
        return {
            ...empty,
            issues: [
                {
                    code: "NO_DAY_HEADER",
                    message: "엑셀에서 요일 행(월, 화, 수, 목, 금)을 찾지 못했습니다.",
                    hint: "1행에 요일을 병합 셀로 적어 주세요. 샘플 양식을 내려받아 형식을 맞춰 보시면 됩니다.",
                },
            ],
        };
    }

    const periodsRowIdx = daysRowIdx + 1;
    const dataStartRow = periodsRowIdx + 1;
    if (dataStartRow >= aoa.length) {
        return {
            ...empty,
            daysRowIdx,
            periodsRowIdx,
            dataStartRow,
            issues: [
                {
                    code: "NO_CLASS_DATA",
                    message: "요일·교시 아래에 학급 수업 데이터가 없습니다.",
                    hint: "3행부터 A열에 학급 이름(예: 1-1)을 넣고, 오른쪽 칸에 수업을 채워 주세요.",
                },
            ],
        };
    }

    const dayMap = buildDayMap(aoa[daysRowIdx] || []);
    const periodMap = buildPeriodMap(aoa[periodsRowIdx] || []);
    if (periodMap.size === 0) {
        return {
            ...empty,
            daysRowIdx,
            periodsRowIdx,
            dataStartRow,
            issues: [
                {
                    code: "NO_PERIOD_ROW",
                    message: "엑셀에서 교시 행(1, 2, 3…)을 찾지 못했습니다.",
                    hint: "요일 바로 아래 행에 각 열의 교시 번호를 숫자로 적어 주세요.",
                },
            ],
        };
    }

    const classCol = findClassCol(aoa, dataStartRow);
    const classroomNames: string[] = [];
    const seenClass = new Set<string>();
    const lessons: ParsedLesson[] = [];

    for (let row = dataStartRow; row < aoa.length; row++) {
        const cName = asString((aoa[row] || [])[classCol]).trim();
        if (!cName || cName.toLowerCase() === "nan") continue;
        if (!seenClass.has(cName)) {
            seenClass.add(cName);
            classroomNames.push(cName);
        }

        for (const [col, periodInt] of periodMap) {
            const day = dayMap.get(col);
            if (!day || !DAY_MARKERS.includes(day as DayMarker)) continue;
            const cellVal = asString((aoa[row] || [])[col]).trim();
            if (isBlank(cellVal)) continue;

            const parsed = parseCell(cellVal);
            if (!parsed.subjectName) continue;
            lessons.push({
                row,
                col,
                classroom: cName,
                day,
                period: periodInt,
                subjectName: parsed.subjectName,
                teacherName: parsed.teacherName,
                parts: parsed.parts,
                raw: cellVal,
            });
        }
    }

    return validateParsedLessons(lessons, {classCol, daysRowIdx, periodsRowIdx, dataStartRow}, classroomNames);
}

export function validateParsedLessons(lessons: ParsedLesson[], metadata: Partial<InspectTimetableResult> = {}, classroomNames = [...new Set(lessons.map(l=>l.classroom))]): InspectTimetableResult {
    const { classCol=0, daysRowIdx=-1, periodsRowIdx=-1, dataStartRow=-1 } = metadata;
    const issues: ExcelImportIssue[] = [];

    if (classroomNames.length === 0) {
        issues.push({
            code: "NO_CLASS_DATA",
            message: "학급 이름(예: 1-1, 1-2)을 찾지 못했습니다.",
            hint: "A열 3행부터 학급을 적어 주세요. 샘플 양식의 학급 열을 그대로 쓰시면 됩니다.",
        });
    }

    if (lessons.length === 0 && classroomNames.length > 0) {
        issues.push({
            code: "NO_LESSONS",
            message: "학급은 있으나 수업 칸이 모두 비어 있습니다.",
            hint: "과목과 교사명을 각 칸에 채워 주세요. 예: 국어 (줄바꿈) 홍길동",
        });
    }

    const teacherSlots = new Map<string, ParsedLesson[]>();
    const classSlots = new Map<string, ParsedLesson[]>();
    for (const lesson of lessons) {
        const tk = `${lesson.teacherName}|${lesson.day}|${lesson.period}`;
        const ck = `${lesson.classroom}|${lesson.day}|${lesson.period}`;
        const tArr = teacherSlots.get(tk);
        if (tArr) tArr.push(lesson);
        else teacherSlots.set(tk, [lesson]);
        const cArr = classSlots.get(ck);
        if (cArr) cArr.push(lesson);
        else classSlots.set(ck, [lesson]);
    }

    for (const group of teacherSlots.values()) {
        const rooms = [...new Set(group.map((g) => g.classroom))];
        if (rooms.length < 2) continue;
        const teacherName = group[0].teacherName;
        const day = group[0].day;
        const period = group[0].period;
        const byClass = rooms.map((classroom) => {
            const hit = group.find((g) => g.classroom === classroom)!;
            return { classroom, subjectName: hit.subjectName };
        });
        issues.push({
            code: "TEACHER_DOUBLE_BOOKED",
            teacherName,
            day,
            period,
            excelRefs: group.map((g) => excelRef(g.row, g.col)),
            message: `${formatDayPeriod(day, period)}에 ${teacherName} 선생님이 ${slotLabel(byClass)}에 동시에 적혀 있습니다.`,
            hint:
                teacherName === "미지정"
                    ? "각 칸에 담당 교사명을 다르게(또는 한 칸만) 적어 주세요."
                    : "한 학급만 남기거나, 다른 학급의 담당 교사를 바꿔 주세요. 두 학급을 한 선생님이 같이 보는 합반도 지금은 한 학급 칸에만 적어 주세요.",
        });
    }

    for (const group of classSlots.values()) {
        if (group.length < 2) continue;
        const classroom = group[0].classroom;
        const day = group[0].day;
        const period = group[0].period;
        const detail = group.map((g) => `${g.subjectName} ${g.teacherName}`).join(", ");
        issues.push({
            code: "CLASSROOM_DOUBLE_BOOKED",
            classroom,
            day,
            period,
            excelRefs: group.map((g) => excelRef(g.row, g.col)),
            message: `${classLabel(classroom)} ${formatDayPeriod(day, period)}에 수업이 ${group.length}개 겹쳐 있습니다 (${detail}).`,
            hint: "같은 요일·교시 열이 중복되지 않았는지 확인하고, 해당 학급에는 수업 하나만 남겨 주세요.",
        });
    }

    for (const lesson of lessons) {
        const ref = excelRef(lesson.row, lesson.col);
        const where = `${classLabel(lesson.classroom)} ${formatDayPeriod(lesson.day, lesson.period)} (엑셀 ${ref})`;
        const shown = lesson.parts.join(" / ");

        if (lesson.parts.length >= 2 && lesson.subjectName === lesson.teacherName) {
            issues.push({
                code: "SUBJECT_EQUALS_TEACHER",
                classroom: lesson.classroom,
                day: lesson.day,
                period: lesson.period,
                teacherName: lesson.teacherName,
                excelRefs: [ref],
                message: `${where}에 과목 없이 교사명만 두 번 적혀 있습니다: "${shown}"`,
                hint: `첫 줄을 과목명으로 바꿔 주세요. 예: 영어 (줄바꿈) ${lesson.teacherName}`,
            });
            continue;
        }

        if (!lesson.subjectName) {
            issues.push({
                code: "MISSING_SUBJECT",
                classroom: lesson.classroom,
                day: lesson.day,
                period: lesson.period,
                excelRefs: [ref],
                message: `${where}에 과목명이 없습니다.`,
                hint: "첫 줄에 과목, 둘째 줄에 교사명을 적어 주세요.",
            });
            continue;
        }

        if (lesson.teacherName === "미지정") {
            issues.push({
                code: "MISSING_TEACHER",
                classroom: lesson.classroom,
                day: lesson.day,
                period: lesson.period,
                subjectName: lesson.subjectName,
                excelRefs: [ref],
                message: `${where}에 과목(${lesson.subjectName})만 있고 교사명이 없습니다.`,
                hint: "둘째 줄(또는 과목 뒤)에 담당 선생님 이름을 적어 주세요. 예: 국어 (줄바꿈) 홍길동",
            });
        }
    }

    return {
        issues: sortIssues(issues),
        lessons,
        classroomNames,
        classCol,
        daysRowIdx,
        periodsRowIdx,
        dataStartRow,
    };
}
